/*
 * Copyright (c) Microsoft Corporation. All rights reserved. Licensed under the MIT license.
 * See LICENSE in the project root for license information.
 */

/* global console, document, Excel, Office */
import constants from "./constants";
import Papa from 'papaparse';
import LogHelper from "./logHelper";

const logHelper = new LogHelper();

// The initialize function must be run each time a new page is loaded
Office.onReady(async () => {
  let info = await Office.onReady();
  if (info.host !== Office.HostType.Excel) {
    return false;
  }
  document.getElementById("sideLoadMsg").style.display = "none";
  document.getElementById("app-body").style.display = "flex";
  // document.getElementById("run").onclick = run;
  // document.getElementById("readCSVFiles").onclick = readCSVFiles;
  const fileList = document.getElementById('fileList');
  //document.getElementById('fileInput').addEventListener('change', handleFiles);
  const dropArea = document.getElementById('dropArea');
  const fileInput = document.getElementById('fileInput');
  const processFileBtn = document.getElementById('processBtn');

  // Global variable 
  window.saleImportSheet = null;

  fileInput.addEventListener('change', handleFiles);
  processFileBtn.addEventListener('click', readCSVFiles)

  dropArea.addEventListener('dragover', (event) => {
    event.preventDefault();
    dropArea.classList.add('dragover');
  });

  dropArea.addEventListener('dragleave', () => {
    dropArea.classList.remove('dragover');
  });

  dropArea.addEventListener('drop', (event) => {
    event.preventDefault();
    dropArea.classList.remove('dragover');
    handleFiles(event);
  });
  // Set only if already not set
  if (!Office.context.document.settings.get("Office.AutoShowTaskpaneWithDocument")) {
    Office.context.document.settings.set("Office.AutoShowTaskpaneWithDocument", true);
    Office.context.document.settings.saveAsync();
  }
});


// Helper function to read file as text using FileReader
function readFileAsText(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = (event) => resolve(event.target.result);
    reader.onerror = (error) => reject(error);
    reader.readAsText(file);
  });
}

// Helper function to parse CSV using PapaParse
function parseCSV(csvData) {
  return new Promise((resolve, reject) => {
    Papa.parse(csvData, {
      header: false, // Set to false if CSV has no headers
      skipEmptyLines: false,
      complete: async (results) => {
        if (results.data.length === 0) {
          console.log("⚠️ CSV is empty!");
          await logHelper.writeLog(logHelper.LogTypes.Warn, "⚠️ CSV is empty!");
          reject([]); // Return an empty array
        } else {
          resolve(results.data);
        }
      },
      error: (error) => reject(error),
    });
  });
}

async function readCSVFiles() {
  await logHelper.writeLog(logHelper.LogTypes.Msg, "Process started to import and export data.");
  const files = document.getElementById("fileInput").files;
  //const status = document.getElementById("status");
  //status.innerHTML = ""; // Clear previous status

  if (files.length === 0) {
    console.log("Please select at least one CSV file.");
    await logHelper.writeLog(logHelper.LogTypes.Warn, `Please select at least one CSV file.`);
    return;
  }

  await logHelper.writeLog(logHelper.LogTypes.Msg, `${files.length} number of files to import and export data.`);
  for (let file of files) {
    try {
      console.log(`Started process for ${file.name} file.`);
      await logHelper.writeLog(logHelper.LogTypes.Msg, `Started process for '${file.name}' file.`);
      const csvData = await readFileAsText(file);
      const parsedData = await parseCSV(csvData);
      await displayData(file.name, parsedData);
      console.log(`Process completed for ${file.name} file.`);
      await logHelper.writeLog(logHelper.LogTypes.Msg, `Process completed for '${file.name}' file.`);
    }
    catch (error) {
      console.log(`Error while processing for ${file.name}. Error details are, `, error);
      await logHelper.writeLog(logHelper.LogTypes.Error, `Error while processing for '${file.name}'.Error details are, ${error} `);
    }
  }
  await logHelper.writeLog(logHelper.LogTypes.Msg, "Process completed to import and export data. Please check your download folder exported files.");
}

async function getSheetByName(context, sheetName) {
  try {
    // if (saleImportSheet) {
    //   return saleImportSheet;
    // }
    let sheet = context.workbook.worksheets.getItem(sheetName);
    sheet.load("name");
    await context.sync();
    console.log(`We found sheet with name ${sheet.name} `);
    await logHelper.writeLog(logHelper.LogTypes.Msg, `We found sheet with name '${sheet.name}'.`);

    return sheet;
  } catch (error) {
    //status.textContent = "Error: " + error.message;
    console.log(`Error while getting sheet with name ${sheetName}. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while getting sheet with name '${sheetName}'. Error details are, ${error}`);
    return null;
  }
}

async function clearSheet(context, sheet) {
  try {
    let usedRange = sheet.getUsedRange();
    usedRange.load("address");
    await context.sync();
    console.log(`Clearing used range: ${usedRange.address} `);
    usedRange.clear(); // Clears content & formatting
    await context.sync();
    console.log("Sheet cleared successfully!");
    await logHelper.writeLog(logHelper.LogTypes.Msg, `Sheet cleaned successfully.`);
  } catch (error) {
    console.log(`Error while Clearing ${constants.ImportDataSheetName} sheet. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while clearing '${constants.ImportDataSheetName}' sheet. Error details are, ${error}`);
  }
  return;
}

async function displayData(fileName, data) {
  try {
    // find max number of columns required to past data
    const maxColumns = Math.max(...data.map(row => row.length));
    data = data.map(row => [...row, ...Array(maxColumns - row.length).fill('')]);
    await Excel.run(async (context) => {
      let saleImportSheet = await getSheetByName(context, constants.ImportDataSheetName);
      if (saleImportSheet) {
        await clearSheet(context, saleImportSheet);
        const rangeAddress = `A1:${String.fromCharCode(65 + maxColumns - 1)}${data.length} `;
        const range = saleImportSheet.getRange(rangeAddress);
        range.values = data;
        range.format.autofitColumns();
        range.format.autofitRows();
        console.log(`CSV successfully copied to '${constants.ImportDataSheetName} sheet.'`);
        await logHelper.writeLog(logHelper.LogTypes.Msg, `CSV successfully copied to '${constants.ImportDataSheetName}' sheet.`);
        await context.sync();
        await exportDataAsCsv(fileName);
      }
    });
  } catch (error) {
    console.log("Error adding csv data to workbook sheets, ", error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error adding csv data to workbook sheets. Error details are, ${error}`);
  }
}

window.removeFile = function (button) {
  const fileName = button.previousSibling.textContent.trim();
  let listItem = button.parentElement;
  listItem.remove();
  updateFileInput(fileName);
  toggleProcessButton();
}

function handleFiles(event) {
  const files = event.target.files || event.dataTransfer.files;
  if (!event.target.files) {
    document.getElementById("fileInput").files = event.dataTransfer.files
  }
  fileList.innerHTML = '';
  // fileInput.value = '';

  Array.from(files).forEach(file => {
    let listItem = document.createElement('li');
    listItem.innerHTML = `${file.name} <button class="remove-btn" onclick="removeFile(this)">×</button>`;
    fileList.appendChild(listItem);
  });
  toggleProcessButton();
}

function toggleProcessButton() {
  const processBtn = document.getElementById('processBtn');
  processBtn.disabled = fileList.children.length === 0;
}

function updateFileInput(filename) {
  let files = [...fileInput.files]
  files = files.filter(file => file.name !== filename);
  const dataTransfer = new DataTransfer();
  if (files) {
    files.forEach(file => dataTransfer.items.add(file));
  }
  fileInput.files = dataTransfer.files;
}

async function isImportedDataValid(context, fileName) {
  try {
    const dateCount = context.workbook.names.getItem(constants.WorkBookNames.DateCount);
    const dateCountRange = dateCount.getRange();
    dateCountRange.load("values");
    const jeBalance = context.workbook.names.getItem(constants.WorkBookNames.JournalEntryBalance);
    const jeBalanceRange = jeBalance.getRange();
    jeBalanceRange.load("values");
    const knownStore = context.workbook.names.getItem(constants.WorkBookNames.KnownStore);
    const knownStoreRange = knownStore.getRange();
    knownStoreRange.load("values");
    await context.sync();
    if (!dateCountRange.isNullObject && dateCountRange.values.length > 0 && dateCountRange.values[0][0] === "Pass"
      && !jeBalanceRange.isNullObject && jeBalanceRange.values.length > 0 && jeBalanceRange.values[0][0] === "Pass"
      && !knownStoreRange.isNullObject && knownStoreRange.values.length > 0 && knownStoreRange.values[0][0] === "Pass") {
      return true;
    }
  } catch (error) {
    console.log(`Error while validating imported csv data for '${fileName}'. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while validating imported csv data for '${fileName}'. Error details are, ${error}`);
  }
  return false;
}

// Get named item value as text if value not found then it will return named name it's self
async function getNamedValueAsText(context, name) {
  try {
    const nameItem = context.workbook.names.getItem(name);
    const nameItemRange = nameItem.getRange();
    nameItemRange.load("values");
    await context.sync();
    if (!nameItemRange.isNullObject && nameItemRange.values.length > 0 && nameItemRange.values[0][0]) {
      return nameItemRange.values[0][0];
    }
  } catch (error) {
    console.log(`Error while getting named value of '${name}'. Error details are, `, error);
    await logHelper.writeLog(logHelper.LogTypes.Error, `Error while getting named value of '${name}'. Error details are, ${error}`);
  }
  return name;
}

async function exportDataAsCsv(fileName) {
  await Excel.run(async (context) => {
    //const sheetName = "Sheet1"; // Change as needed
    try {
      await logHelper.writeLog(logHelper.LogTypes.Msg, `Data refresh triggered. Waiting for completion...`);
      // Force recalculation of all formulas
      context.workbook.application.calculate(Excel.CalculationMode.full);
      await context.sync();
      await logHelper.writeLog(logHelper.LogTypes.Msg, `Data refreshed.`);
      await logHelper.writeLog(logHelper.LogTypes.Msg, `Check data validation.`);
      const names = context.workbook.names;
      names.load("items"); // Load all named items
      await context.sync();
      let isDataValid = await isImportedDataValid(context, fileName);
      if (isDataValid) {
        await logHelper.writeLog(logHelper.LogTypes.Msg, `Data is valid.`);
        await logHelper.writeLog(logHelper.LogTypes.Msg, `Started to export data as csv file for '${fileName}'.`);
        // Get the sheet by name
        let sheet = await getSheetByName(context, constants.ExportDataSheetName);;
        let usedRange = sheet.getUsedRange();

        // Refresh all data connections (PivotTables, Queries, etc.)
        console.log("Data refresh triggered. Waiting for completion...");
        usedRange.load("values"); // Load cell values
        await context.sync();

        let classValue = await getNamedValueAsText(context, constants.WorkBookNames.Class);
        let startDateValue = await getNamedValueAsText(context, constants.WorkBookNames.StartDate);
        // convert excel date number to date and time format
        if (startDateValue && typeof startDateValue === "number") {
          startDateValue = new Date(Date.UTC(1899, 11, 30) + startDateValue * 24 * 60 * 60 * 1000);
          startDateValue = getDateInStringFormat(startDateValue);
        }

        // Convert the data to CSV using PapaParse
        let csvContent = Papa.unparse(usedRange.values);

        // Create a Blob and download the file
        let blob = new Blob([csvContent], { type: "text/csv" });
        let url = URL.createObjectURL(blob);

        let a = document.createElement("a");
        a.href = url;
        a.download = `${classValue}_JE Imported_${startDateValue}.csv`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        console.log("CSV file exported successfully!");
        await logHelper.writeLog(logHelper.LogTypes.Msg, `CSV file exported successfully for '${fileName}'.`);
      }
      else {
        await logHelper.writeLog(logHelper.LogTypes.Error, `Imported data are not valid. Skip export for '${fileName}'.`);
      }
    } catch (error) {
      console.log(`Error while exporting csv data for '${fileName}'. Error details are, `, error);
      await logHelper.writeLog(logHelper.LogTypes.Error, `Error while exporting csv data for '${fileName}'. Error details are, ${error}`);
    }
  });
}

function getDateInStringFormat(date) {
  const day = String(date.getDate()).padStart(2, '0');
  const monthNames = ["Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
  const month = monthNames[date.getMonth()];
  const year = date.getFullYear();
  return `${day}-${month}-${year}`;
}